import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import CardSelector, { useCardVisibility } from '~/components/CardSelector'
import {
  formatDateToString,
  getAccountId,
  getRetailMediaAccountType,
  getWhiteLabelPublisherId,
  isAdvertiser
} from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import useQueryParams from '~/hooks/useQuery'
import Card from '~/modules/retailMedia/components/Card'
import Chart from '~/modules/retailMedia/components/Chart'
import WrapperChartAndCards from '~/modules/retailMedia/components/WrapperChartAndCards'
import { parseLookbackMonths } from '~/modules/retailMedia/constants'
import { getNtbOrdersWindow } from '~/modules/retailMedia/helpers/newMetricsAvailability'
import { NTB_CARD_TYPES } from '~/modules/retailMedia/helpers/ntbMetrics'
import { buildRoasAttributionChartMarker } from '~/modules/retailMedia/helpers/roasAttributionChartMarker'
import { useClickMetricsCutoffAlert } from '~/modules/retailMedia/hooks/useClickMetricsCutoffAlert'
import { useNtbAvailability } from '~/modules/retailMedia/hooks/useNtbAvailability'
import { useNtbMetricsEnabled } from '~/modules/retailMedia/hooks/useNtbMetricsEnabled'
import {
  reportCampaigns,
  reportCampaignsCompare,
  reportCampaignsHistory,
  reportCampaignsHistoryCompare
} from '~/modules/retailMedia/store/report/actions'
import { useAppSelector } from '~/store/hooks'

import cardsByType from './cards'
import { dashboardIcons } from './cards/icons'

import * as S from './styles'

const OPTIONAL_CARD_TYPES = new Set([
  'campaigns',
  'actives_campaigns',
  'actives_advertisers',
  'ctr',
  'income',
  'conversion_rate_click',
  'conversions_click',
  'sales_click',
  'halo_revenue',
  'halo_orders',
  'halo_items',
  'roas_click',
  'roas_view',
  'roas_halo',
  'roas_overall',
  'impression_share',
  'lost_is_rank',
  'lost_is_budget',
  ...NTB_CARD_TYPES
])

type DashboardRetailMediaCampaignsProps = {
  origin?: DashboardRetailMediaOrigin
}

const DashboardRetailMediaCampaigns: React.FC<
  DashboardRetailMediaCampaignsProps
> = ({ origin }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const accountType = getRetailMediaAccountType()

  const searchParams = useQueryParams()

  const paramId = searchParams.get('rmid')
  const advertiser_name = searchParams.get('advertiser_name')
  const publisher_name = searchParams.get('publisher_name')
  const seller_id = searchParams.get('seller_id')
  const ad_type = searchParams.get('ad_type')
  const campaign_status = searchParams.get('campaign_status')
  const campaign_name = searchParams.get('campaign_name')
  const tag_id = searchParams.get('tag_id') as PublisherTagId
  const targeting_type = searchParams.get(
    'campaign_targeting'
  ) as RetailMediaCampaignSegmentation
  const product_sku = searchParams.get('product_sku') as RetailMediaAdsStatuses
  const ad_status = searchParams.get('ad_status') as RetailMediaAdsStatuses
  const lookback_months = searchParams.get('lookback_months')
  const lookbackMonths = parseLookbackMonths(lookback_months)

  const { isWhiteLabel } = useAppInfo()

  const id = useMemo(
    () =>
      isWhiteLabel && isAdvertiser() ? getWhiteLabelPublisherId() : paramId,
    [isWhiteLabel, paramId]
  )

  // o entitlement de IS precisa de um publisher só: agregado de vários
  // (lista de publishers sem rmid, sub_publisher) segue pelo payload
  const impressionSharePublisherId = useMemo(() => {
    if (origin === 'sub_publisher') return null

    if (accountType === 'advertiser') return id

    return getAccountId()
  }, [origin, accountType, id])

  /**
   * Redux states
   */
  const { startDate, endDate, compareStartDate, compareEndDate } =
    useAppSelector(state => state.datepickerHeader)
  const hasCompareInterval = !!compareStartDate && !!compareEndDate

  const {
    report: {
      get: { data: cardData, loading: cardLoading },
      getHistory: { data: chartData, loading: chartLoading, error: chartError },
      getCompare: { data: cardDataCompare },
      getHistoryCompare: { data: chartDataCompare }
    }
  } = useAppSelector(state => state.retailMedia)

  /**
   * 1. Requisição para os dados dos cards
   * 2. Requisição para os dados do gráfico
   */
  const requestKeyId = useMemo(() => {
    if (origin === 'sub_publisher') return 'publisher_id'
    return accountType === 'publisher' ? 'advertiser_id' : 'publisher_id'
  }, [origin, accountType])

  // Origin advertiser
  const advertisersParams = useMemo(
    () => ({
      advertiser_name,
      seller_id,
      tag_id
    }),
    [advertiser_name, seller_id, tag_id]
  )

  // Origin publisher
  const publishersParams = useMemo(
    () => ({
      publisher_name: publisher_name || null
    }),
    [publisher_name]
  )

  //  Origin campaigns
  const campaignsParams = useMemo(
    () => ({
      ad_type,
      campaign_status,
      campaign_name,
      tag_id,
      targeting_type
    }),
    [ad_type, campaign_name, campaign_status, tag_id, targeting_type]
  )

  //  Origin ads
  const adsParams = useMemo(
    () => ({
      ad_type,
      ad_status: ad_status,
      tag_id,
      targeting_type,
      // API não suporta ainda
      product_sku: product_sku || null
    }),
    [ad_status, ad_type, product_sku, tag_id, targeting_type]
  )

  const queryParams = useMemo(
    () =>
      ({
        [requestKeyId]: id,
        start_date: formatDateToString(startDate, 'YYYY-MM-DD'),
        end_date: formatDateToString(endDate, 'YYYY-MM-DD'),
        lookback_months: lookbackMonths,
        ...(origin === 'advertiser' ? advertisersParams : {}),
        ...(origin === 'publisher' ? publishersParams : {}),
        ...(origin === 'campaign' ? campaignsParams : {}),
        ...(origin === 'ads' ? adsParams : {})
      }) as GetReportCampaignsActionProps,
    [
      requestKeyId,
      id,
      startDate,
      endDate,
      lookbackMonths,
      origin,
      advertisersParams,
      publishersParams,
      campaignsParams,
      adsParams
    ]
  )

  // query params for compare with another interval
  const queryParamsCompare = useMemo(() => {
    return {
      [requestKeyId]: id,
      start_date: formatDateToString(compareStartDate, 'YYYY-MM-DD'),
      end_date: formatDateToString(compareEndDate, 'YYYY-MM-DD'),
      lookback_months: lookbackMonths,
      compare: true,
      ...(origin === 'advertiser' ? advertisersParams : {}),
      ...(origin === 'publisher' ? publishersParams : {}),
      ...(origin === 'campaign' ? campaignsParams : {}),
      ...(origin === 'ads' ? adsParams : {})
    }
  }, [
    requestKeyId,
    id,
    compareStartDate,
    compareEndDate,
    lookbackMonths,
    origin,
    advertisersParams,
    publishersParams,
    campaignsParams,
    adsParams
  ])

  useEffect(() => {
    dispatch(reportCampaigns(queryParams))
    dispatch(reportCampaignsHistory(queryParams))
  }, [dispatch, queryParams])

  useEffect(() => {
    if (hasCompareInterval) {
      dispatch(reportCampaignsCompare(queryParamsCompare))
      dispatch(reportCampaignsHistoryCompare(queryParamsCompare))
    }
  }, [queryParamsCompare, hasCompareInterval, dispatch])

  /**
   * Configuração dos cards
   */
  const icons = useMemo(() => dashboardIcons[accountType], [accountType])
  // NTB é um agregado por publisher: só existe para anunciante ancorado em um
  // (rmid ou white-label), e ainda passa pelo gate por conta
  const isNtbScope = isAdvertiser() && !!id
  const cardSources = useMemo(
    () => [cardData, cardDataCompare].filter(Boolean),
    [cardData, cardDataCompare]
  )
  const availableMetrics = useNtbAvailability(cardSources, {
    anchored: isNtbScope,
    impressionShare: {
      publisherId: impressionSharePublisherId,
      advertiserId: accountType === 'advertiser' ? getAccountId() : null
    }
  })
  const showClickAlert = useClickMetricsCutoffAlert({ checkCompare: true })

  const ntbMetricsEnabled = useNtbMetricsEnabled()
  // série de NTB na listagem de campanhas de um publisher (grão de publisher):
  // mesma ancoragem e mesmo gate do card
  const excludeChartMetrics = useMemo(
    () =>
      origin === 'campaign' && ntbMetricsEnabled && isNtbScope
        ? []
        : (['newToBrandCustomers', 'newToBrandRevenue'] as AvailablePresets[]),
    [origin, ntbMetricsEnabled, isNtbScope]
  )

  const ntbOrdersWindow = useMemo(() => getNtbOrdersWindow(endDate), [endDate])

  const cards = useMemo(() => {
    const formatter = id ? cardsByType : cardsByType.listAll

    const formatted = formatter[accountType]({
      availableMetrics,
      showClickAlert,
      lookbackMonths,
      ntbOrdersWindow,
      data: cardData,
      compareData: cardDataCompare,
      isCompare: hasCompareInterval,
      loading: cardLoading
    })

    return formatted
  }, [
    accountType,
    cardData,
    cardDataCompare,
    cardLoading,
    lookbackMonths,
    ntbOrdersWindow,
    hasCompareInterval,
    availableMetrics,
    showClickAlert,
    id
  ])

  const screenName = useMemo(
    () => `${id ? 'publisher' : 'all'}-${origin}-${accountType}`,
    [id, origin, accountType]
  )

  const defaultVisible = useMemo(
    () =>
      cards
        .map(card => card.type)
        .filter(type => !OPTIONAL_CARD_TYPES.has(type)),
    [cards]
  )

  const {
    cardsToShow,
    cardsToCustomize,
    hiddenCards,
    defaultCardsToCustomize,
    defaultHiddenCards,
    saveCustomization,
    resetCustomization
  } = useCardVisibility({
    cards,
    screenName,
    defaultVisible
  })

  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false)

  const chartMarker = useMemo(
    () =>
      buildRoasAttributionChartMarker({
        data: chartData,
        compareData: chartDataCompare,
        isCompare: hasCompareInterval,
        t
      }),
    [chartData, chartDataCompare, hasCompareInterval, t]
  )

  const handleOpenCustomize = useCallback(() => {
    setIsCustomizeOpen(true)
  }, [])

  const handleCloseCustomize = useCallback((callback?: () => void) => {
    callback?.()
    setIsCustomizeOpen(false)
  }, [])

  return (
    <WrapperChartAndCards>
      <Chart
        chartData={chartData}
        chartDataCompare={chartDataCompare}
        isCompare={hasCompareInterval}
        marker={chartMarker}
        // onCompareDate={handleCompareDate}
        compareInterval={{ start: compareStartDate, end: compareEndDate }}
        loading={chartLoading}
        error={chartError}
        initialMetrics={['clicks', 'impressions', 'conversions']}
        additionalMetricsKeys={['totalSpent', 'income']}
        selectQuantity={3}
        excludeMetrics={excludeChartMetrics}
      />

      <div className="wrapperCards">
        <S.WrapperCard>
          <div className="hold-cards">
            {cardsToShow.map((item, index) => (
              <Card
                key={index}
                title={item.title}
                value={item.value}
                compareValue={item.compareValue}
                loading={item.loading}
                icon={icons?.[item.type]}
                isRelativeInterval={item.isRelativeInterval}
                badgeOverride={item.badge}
                compareInterval={{
                  startDate: compareStartDate,
                  endDate: compareEndDate
                }}
                isCompare={hasCompareInterval}
              />
            ))}
          </div>

          <div className="d-flex justify-content-end mt-3">
            <CardSelector.ToggleButton onClick={handleOpenCustomize} />
          </div>
        </S.WrapperCard>
      </div>

      <CardSelector.Drawer
        cards={cardsToCustomize}
        hiddenCards={hiddenCards}
        defaultCardsToCustomize={defaultCardsToCustomize}
        defaultHiddenCards={defaultHiddenCards}
        isOpen={isCustomizeOpen}
        onClose={handleCloseCustomize}
        onChange={saveCustomization}
        onReset={resetCustomization}
      />
    </WrapperChartAndCards>
  )
}

export default DashboardRetailMediaCampaigns
